import { useState, useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { StringParam } from 'use-query-params'
import { useUserContext } from 'apps/fleet-planner/context'
import { useUserAuth } from 'apps/fleet-planner/hooks'
import { useShipmentOffersApiDeprecated } from 'apps/fleet-planner/hooks/useShipmentOffersApiDeprecated'
import { useQueryParams } from 'utils/hooks'

/**
 * Uses query params to trigger auth and decline workflow from email link
 */
export const useDeclineOffer = () => {
  const history = useHistory()
  const { userData } = useUserContext()
  const { user } = userData || {}
  const { handleTokenLogin } = useUserAuth()
  const { declineShipmentOffer } = useShipmentOffersApiDeprecated()
  const { shipmentId } = useParams<{ shipmentId: string }>()
  const [{ token }] = useQueryParams<{ token: string; shipmentId: number }>({
    token: StringParam,
  })

  const [authErrorMessage, setAuthErrorMessage] = useState<string>()

  // Begin this flow with loading as true
  const [isLoading, setLoading] = useState<boolean>(true)

  /**
   * Main function, which uses auth and decline methods
   */
  const loginAndDecline = useCallback(async () => {
    let isLoggedIn = user !== null
    let declineOfferStatus = 'failure'

    setAuthErrorMessage(undefined)

    /**
     * If user is not yet logged in, authenticate with `token` param
     */
    if (!isLoggedIn) {
      isLoggedIn = await handleTokenLogin(token)
    }

    /**
     * Decline offer programmatically and send
     * the user to home page
     */
    let declinedOfferId: number | undefined
    if (isLoggedIn && shipmentId) {
      declinedOfferId = await declineShipmentOffer(Number(shipmentId))
      declineOfferStatus = declinedOfferId ? 'success' : 'failure'
    }

    // Expected type on success is number
    if (typeof declinedOfferId !== 'number') {
      setAuthErrorMessage('Encountered an error when attempting to login')
    }

    setLoading(false)

    /**
     * Route to home page if decline succeeds or fails
     */
    history.push(`/home?declineOfferStatus=${declineOfferStatus}`)
  }, [declineShipmentOffer, handleTokenLogin, history, shipmentId, token, user])

  return {
    loginAndDecline,
    authErrorMessage,
    isLoading,
  }
}
